import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { MapPin, Palette, ArrowRight, Compass } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { SILK_ROAD_REGIONS } from '@/data/silkRoadRegions';

const API = process.env.REACT_APP_BACKEND_URL || '';

const RegionCard = ({ region, artistCount, index }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  return (
    <div
      className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer"
      data-testid={`region-card-${region.slug}`}
      style={{ animationDelay: `${index * 80}ms` }}
      onClick={() => navigate(`/explore/${region.slug}`)}
    >
      <img
        src={region.image}
        alt={t(`silkRoad.regions.${region.slug}.name`)}
        className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
        loading="lazy"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-black/10 transition-all duration-500 group-hover:from-black/95 group-hover:via-black/50" />

      <div className="absolute inset-0 flex flex-col justify-end p-5 md:p-6">
        <p className="text-amber-400 text-[11px] tracking-wider uppercase mb-1.5 font-medium">
          {t(`silkRoad.regions.${region.slug}.tagline`)}
        </p>
        <h3 className="text-white text-xl md:text-2xl font-bold mb-2 leading-tight">
          {t(`silkRoad.regions.${region.slug}.name`)}
        </h3>
        <p className="text-slate-300 text-sm leading-relaxed line-clamp-3 md:max-h-0 md:opacity-0 group-hover:md:max-h-40 group-hover:md:opacity-100 transition-all duration-500 overflow-hidden">
          {t(`silkRoad.regions.${region.slug}.description`)}
        </p>
        <div className="mt-3 flex items-center gap-2">
          {artistCount > 0 ? (
            <span className="inline-flex items-center gap-1.5 text-amber-400/80 text-xs">
              <Palette className="w-3 h-3" />
              {t('silkRoad.artistCount', { count: artistCount })}
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-slate-500 text-xs">
              <MapPin className="w-3 h-3" />
              {t('silkRoad.noArtistsYet')}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const SilkRoadExplorer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [regionData, setRegionData] = useState({});

  useEffect(() => {
    axios.get(`${API}/api/featured-artists/regions`)
      .then(res => {
        const data = {};
        (res.data || []).forEach(r => { data[r.slug] = r; });
        setRegionData(data);
      })
      .catch(() => {});
  }, []);

  return (
    <section className="bg-slate-950 py-20 md:py-28 px-6 relative overflow-hidden" data-testid="silk-road-explorer">
      <div className="absolute inset-0 opacity-[0.02]" style={{
        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23d4a574' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
      }} />

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-3 mb-6">
            <div className="h-px w-12 bg-amber-600/40" />
            <div className="flex items-center gap-2 text-amber-500">
              <Compass className="w-4 h-4" />
              <span className="text-xs tracking-[0.3em] uppercase font-medium">
                {t('silkRoad.badge')}
              </span>
            </div>
            <div className="h-px w-12 bg-amber-600/40" />
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 tracking-tight" data-testid="silk-road-title">
            {t('silkRoad.title')}
          </h2>

          <p className="text-slate-400 max-w-3xl mx-auto text-base leading-relaxed">
            {t('silkRoad.subtitle')}
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {SILK_ROAD_REGIONS.map((region, idx) => (
            <RegionCard
              key={region.slug}
              region={region}
              artistCount={(regionData[region.slug] || {}).artist_count || 0}
              index={idx}
            />
          ))}

          <div
            className="group relative overflow-hidden rounded-2xl aspect-[3/4] cursor-pointer bg-gradient-to-br from-amber-900/20 to-slate-900 border border-amber-800/20 hover:border-amber-600/40 transition-all duration-500 flex flex-col items-center justify-center p-6 text-center"
            onClick={() => navigate('/login')}
            data-testid="silk-road-join-cta"
          >
            <div className="w-16 h-16 rounded-full bg-amber-500/10 flex items-center justify-center mb-5 group-hover:bg-amber-500/20 transition-colors duration-300">
              <Palette className="w-8 h-8 text-amber-500" />
            </div>
            <h3 className="text-white text-lg font-bold mb-3">
              {t('silkRoad.joinAsArtist')}
            </h3>
            <p className="text-slate-500 text-sm leading-relaxed mb-5">
              {t('silkRoad.joinDescription')}
            </p>
            <div className="inline-flex items-center gap-1.5 text-amber-500 text-sm font-medium group-hover:gap-3 transition-all duration-300">
              {t('silkRoad.discoverMore')}
              <ArrowRight className="w-4 h-4" />
            </div>
          </div>
        </div>

        <div className="text-center mt-14">
          <Button
            size="lg"
            className="bg-amber-600 hover:bg-amber-500 text-white px-8 py-6 rounded-xl font-medium tracking-wide"
            onClick={() => navigate('/artcanvas')}
            data-testid="silk-road-explore-btn"
          >
            {t('silkRoad.exploreGallery')}
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default SilkRoadExplorer;
